import { apiClient } from "@/lib/api/client"

type PublicKeyPayload = {
  algorithm: "RSA-OAEP"
  hash: "SHA-256"
  publicKey: string
}

let cached: { key: CryptoKey; expiresAt: number } | null = null
const TTL_MS = 5 * 60 * 1000

function b64ToBuf(b64: string): ArrayBuffer {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return bytes.buffer
}

function bufToB64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let s = ""
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i])
  return btoa(s)
}

// Comma-separated base64 SHA-256 fingerprints of the API's SPKI public key.
// Multiple values allow overlap during key rotation.
const PINNED_SPKI_SHA256 = (process.env.NEXT_PUBLIC_CREDENTIAL_KEY_SHA256 ?? "")
  .split(",")
  .map((s) => s.trim())
  .filter(Boolean)

async function assertPinned(spki: ArrayBuffer): Promise<void> {
  if (PINNED_SPKI_SHA256.length === 0) {
    if (process.env.NODE_ENV === "production") {
      throw new Error("NEXT_PUBLIC_CREDENTIAL_KEY_SHA256 is not configured")
    }
    return
  }
  const digest = bufToB64(await crypto.subtle.digest("SHA-256", spki))
  if (!PINNED_SPKI_SHA256.includes(digest)) {
    throw new Error("Credential public key does not match pinned fingerprint")
  }
}

async function getPublicKey(): Promise<CryptoKey> {
  if (cached && Date.now() < cached.expiresAt) return cached.key
  const res = await apiClient.get<PublicKeyPayload>(
    "/auth/credential-public-key"
  )
  const spki = b64ToBuf(res.data.publicKey)
  await assertPinned(spki)
  const key = await crypto.subtle.importKey(
    "spki",
    spki,
    { name: "RSA-OAEP", hash: "SHA-256" },
    false,
    ["encrypt"]
  )
  cached = { key, expiresAt: Date.now() + TTL_MS }
  return key
}

export async function encryptPassword(plaintext: string): Promise<string> {
  const key = await getPublicKey()
  const cipher = await crypto.subtle.encrypt(
    { name: "RSA-OAEP" },
    key,
    new TextEncoder().encode(plaintext)
  )
  return bufToB64(cipher)
}

/** Cache bust on key rotation errors */
export function clearCredentialPublicKeyCache() {
  cached = null
}
